// @ts-nocheck

import React, { useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import usePrevious from '../../hooks/usePrevious';
import { __ } from '../../i18n/l10n';
import BoomBasketBetslipBandContainer from '../../modules/boom/components/BoomBasketBetslip/BoomBasketBetslipContainer';
import GRBasketBetslipEligibleHeadBandContainer from '../../modules/gift-race/containers/GRBasketBetslipEligibleHeadBandContainer';
import KLBasketBetslipEligibleCollectibleContainer from '../../modules/koh-lanta/containers/KLBasketBetslipEligibleCollectibleContainer';
import BasketBetslipMissionEligibleTagContainer from '../../modules/missions/components/BasketBetslipMissionEligibleTag/BasketBetslipMissionEligibleTagContainer';
import MPBasketBetslipBandContainer from '../../modules/monopoly/containers/MPBasketBetslipBandContainer';
import theme from '../../styled-components/dark';
import { withOpacity } from '../../styled-components/dark/helpers';
import { BasketModes } from '../../types/basket';
import { type BasketStatus } from '../../types/basket';
import { type BasketMode } from '../../types/basket';
import BasketAlert from '../BasketAlert';
import BasketCardContainer from '../BasketCard/BasketCardContainer';
import BasketEmptyView from '../BasketEmptyView';
import BasketFooterContainer from '../BasketFooter/BasketFooterContainer';
import BasketHeaderContainer from '../BasketHeader/BasketHeaderContainer';
import WinaLoader from '../WinaLoader';

const Wrapper = styled.div`
  height: 100%;
  border-radius: 10px;
  overflow: hidden;
`;

const ScrollView = styled.div`
  /* fix flash on chrome when switching between fixed and absolute */
  transform: translateZ(0);
`;

const Body = styled.div`
  background-color: ${withOpacity(theme.Basket.contentBackgroundColor, 'aa')};
`;

const Content = styled.div``;

const AddedCashoutItems = styled.div`
  background-color: ${({ $highlight }) => ($highlight ? theme.Basket.addedCashoutItemsBackgroundColor : 'inherit')};
  padding-bottom: 0.6em;
`;

const CashoutItemsSeparator = styled.div`
  background-color: ${theme.Basket.separatorCashoutItemsBackgroundColor};
  padding: 0.2em 0.6em;
  margin-top: 0.6em;
  color: #fff;
`;

const StyledAlert = styled(BasketAlert)`
  margin: 0.6em 0.6em 0.2em;
`;

const StyledLoader = styled(WinaLoader)`
  margin: 0;
  padding: 5em 0em;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 9em;
  color: #fff;
`;

const StyledEmptyBasket = styled(BasketEmptyView)`
  margin-top: ${Target.__MOBILE__ ? undefined : '0em'};
`;

type SimpleItem = {
  id: number;
  isCashout: boolean;
};

type BasketItemsType = {
  items: Array<SimpleItem>;
  title: string | null;
  isValidating: boolean;
};

const BasketItems = ({ items, title, isValidating }: BasketItemsType) => {
  return (
    <>
      {items
        .filter((item) => item.isCashout)
        .map((item) => (
          <BasketCardContainer key={item.id} itemId={item.id} isValidating={isValidating} />
        ))}
      {title ? <CashoutItemsSeparator>{title}</CashoutItemsSeparator> : null}
      <AddedCashoutItems $highlight={!!title}>
        {items
          .filter((item) => !item.isCashout)
          .map((item) => (
            <BasketCardContainer key={item.id} itemId={item.id} isValidating={isValidating} />
          ))}
      </AddedCashoutItems>
    </>
  );
};

const marginSharedStyle = css`
  margin: 0.6em 0.6em 0 0.6em;
`;

const StyledGRBasketBetslipEligibleHeadBandContainer = styled(GRBasketBetslipEligibleHeadBandContainer)`
  ${marginSharedStyle}
`;

const StyledKLBasketBetslipEligibleCollectibleContainer = styled(KLBasketBetslipEligibleCollectibleContainer)`
  ${marginSharedStyle}
`;

const StyledBasketBetslipMissionEligibleTagContainer = styled(BasketBetslipMissionEligibleTagContainer)`
  ${marginSharedStyle}
`;

const StyledMPBasketBetslipContainer = styled(MPBasketBetslipBandContainer)`
  ${marginSharedStyle}
`;

const StyledBoomBasketBetslipBandContainer = styled(BoomBasketBetslipBandContainer)`
  ${marginSharedStyle}
`;

type Props = {
  items: Array<SimpleItem>;
  title: string;
  isCashout: boolean;
  isLoading: boolean;
  isValidating: boolean;
  isEmpty: boolean;
  alertContent: string;
  alertStatus: BasketStatus;
  betshareBetslipReference: string | null;
  closeModal?: () => void;
  isBetshareOpen: boolean;
  isShowingMainBetConfirmation: boolean;
  basketMode: BasketMode;
};

const Basket = ({
  items,
  title,
  isCashout,
  isLoading,
  isValidating,
  isEmpty,
  alertContent,
  alertStatus,
  betshareBetslipReference,
  closeModal,
  isBetshareOpen,
  isShowingMainBetConfirmation,
  basketMode,
}: Props) => {
  const prevItems = usePrevious(items);

  useEffect(() => {
    if (
      Target.__MOBILE__ &&
      items &&
      prevItems &&
      items.length === 0 &&
      prevItems.length > 0 &&
      closeModal &&
      !isBetshareOpen
    ) {
      closeModal();
    }
  }, [items, prevItems, closeModal, isBetshareOpen]);

  const showContent = useMemo(() => !isEmpty && !isLoading, [isEmpty, isLoading]);

  return (
    <Wrapper>
      {!Target.__MOBILE__ ? <BasketHeaderContainer /> : null}
      <Body>
        {isEmpty ? (
          <Empty>
            <StyledEmptyBasket label={__('Your betslip is empty!')} subLabel={__('!desc:BasketHowTo')} />
          </Empty>
        ) : null}
        {isLoading ? <StyledLoader /> : null}
        {showContent ? (
          <Content>
            <ScrollView>
              {alertContent && !isShowingMainBetConfirmation ? (
                <StyledAlert message={alertContent} status={alertStatus} betslipReference={betshareBetslipReference} />
              ) : null}
              <StyledGRBasketBetslipEligibleHeadBandContainer />
              <StyledKLBasketBetslipEligibleCollectibleContainer />
              <StyledMPBasketBetslipContainer />
              <StyledBoomBasketBetslipBandContainer />
              {basketMode === BasketModes.COMBINED ? <StyledBasketBetslipMissionEligibleTagContainer /> : null}
              <BasketItems title={isCashout ? title : null} items={items} isValidating={isValidating} />
            </ScrollView>
          </Content>
        ) : null}
      </Body>
      {!Target.__MOBILE__ ? <BasketFooterContainer /> : null}
    </Wrapper>
  );
};

export default Basket;
